import moment from 'moment'
import { emailRegex } from 'utils/helpers'

import { FIELDS_TO_REMOVE } from './constants'

// Arrange subscriptions based on the selected package and the monthly rate
export const arrangeSubscriptions = formData => {
  const {
    selectedPackage,
    monthlyRate,
    setupFee,
    hasSetupFee,
    premiumSupportFee,
    hasPremiumSupport
  } = formData
  const subscriptionConfigs = [
    {
      plan: 'directfulCore',
      fee: selectedPackage === 'unlimited' ? 0 : monthlyRate || 0,
      always: true
    },
    {
      plan: 'unlimited',
      fee: monthlyRate || 0,
      condition: selectedPackage === 'unlimited'
    },
    {
      plan: 'setupFee',
      fee: setupFee || '',
      condition: hasSetupFee
    },
    {
      plan: 'premiumSupport',
      fee: premiumSupportFee || '',
      condition: hasPremiumSupport
    }
  ]

  return subscriptionConfigs
    .filter(sub => sub.always || sub.condition)
    .map(({ plan, fee }) => ({ plan, fee }))
}

// Validate the add hotel form
export const validateAddHotelForm = (formData, addToast) => {
  const validations = [
    {
      condition: !formData.monthlyRate || formData.monthlyRate <= 0,
      message: 'Please enter a valid monthly package fee'
    },
    {
      condition: !formData.salesPersonUserId,
      message: 'Please select sales agent'
    },
    {
      condition: !formData.csmId,
      message: 'Please select CSM'
    },
    {
      condition: formData.numberOfRooms <= 0,
      message: 'Number of rooms must be filled'
    },
    {
      condition: !formData.supportEmail || !formData.supportEmail.match(emailRegex),
      message: 'Please enter a valid support email'
    },
    {
      condition:
        !formData.supportPhone ||
        formData.supportPhone.replace(/[^0-9]+/g, '').length <= 6,
      message: 'Please enter a valid support phone number'
    },
    {
      condition: !(
        formData.parentChainCode &&
        formData.chainCode &&
        formData.pms &&
        formData.salesPersonUserId &&
        formData.csmId
      ),
      message: 'Please fill out all fields'
    },
    {
      condition: formData.hasSetupFee && (!formData.setupFee || formData.setupFee <= 0),
      message: 'Please enter a valid setup fee amount'
    },
    {
      condition:
        formData.hasPremiumSupport &&
        (!formData.premiumSupportFee || formData.premiumSupportFee <= 0),
      message: 'Please enter a valid premium support fee amount'
    }
  ]

  const validateUsers = () => {
    // Check for missing fields
    const hasMissingFields = formData.newUsers.some(user => {
      const missingFields = []
      if (!user.fullName) missingFields.push('Full Name')
      if (!user.email) missingFields.push('Email')
      if (!user.timeZoneName) missingFields.push('Time Zone')

      if (missingFields.length > 0) {
        const userRole = user.isGeneralManager
          ? 'General Manager'
          : 'Account Billable Contact'
        addToast(
          `Please fill out required fields for ${userRole}: ${missingFields.join(', ')}`,
          { appearance: 'error' }
        )
        return true
      }
      return false
    })

    if (hasMissingFields) {
      return true
    }

    // Check for duplicate emails between GM and Account Billable
    const generalManager = formData.newUsers.find(user => user.isGeneralManager)
    const accountBillable = formData.newUsers.find(
      user => user.isAccountBillable && !user.isGeneralManager
    )

    if (
      generalManager &&
      accountBillable &&
      generalManager.email === accountBillable.email &&
      !generalManager.isAccountBillable
    ) {
      addToast(
        'The General Manager and Account Billable Contact cannot have the same email address',
        { appearance: 'error' }
      )
      return true
    }

    return false
  }

  // Check basic validations
  const failedValidation = validations.find(v => v.condition)
  if (failedValidation) {
    addToast(failedValidation.message, { appearance: 'error' })
    return false
  }

  // Check user validations
  if (validateUsers()) {
    return false
  }

  return true
}

// Helper to remove empty/null/undefined values
const removeEmptyValues = obj => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const isValidValue = value !== '' && value !== null && value !== undefined
    return isValidValue ? { ...acc, [key]: value } : acc
  }, {})
}

export const prepareFormDataForSubmission = formData => {
  const formattedData = {
    ...formData,
    newUsers: formData?.newUsers?.map(user => ({
      ...user,
      fullName: user.fullName.trim()
    })),
    serviceStartDate: formData.serviceStartDate
      ? moment(formData.serviceStartDate).format('YYYY-MM-DD')
      : undefined
  }

  const withSubscriptions = {
    ...formattedData,
    subscriptions: arrangeSubscriptions(formattedData)
  }

  // Remove temporary fields used for UI
  const withoutTempFields = Object.keys(withSubscriptions)
    .filter(key => !FIELDS_TO_REMOVE.includes(key))
    .reduce((acc, key) => ({ ...acc, [key]: withSubscriptions[key] }), {})

  // Remove empty values
  return removeEmptyValues(withoutTempFields)
}

// Handle discount change with validation
export const handleDiscountChange = (e, monthlyRate, setFormData) => {
  const inputDiscount = Number(e.target.value)
  if (inputDiscount <= monthlyRate) {
    setFormData({ discount: inputDiscount })
  } else {
    setFormData({ discount: monthlyRate })
  }
}

// Check service start date and show appropriate warnings
export const checkDate = (serviceStartDate, addToast, setFormData) => {
  const date = moment(serviceStartDate).format('YYYY-MM-DD')
  const selectMonthName = moment(date).format('MMMM')
  const nextMonthName = moment(date).add(1, 'months').format('MMMM')
  const todayDay = moment(date).date()

  // Calculate days remaining after removing full months
  const targetDate = moment(date)
  const monthsDiff = targetDate.diff(moment().format('YYYY-MM-DD'), 'months')
  const daysAfterFullMonths = targetDate.diff(moment().add(monthsDiff, 'months'), 'days')
  const diffDays = moment(date).diff(moment().format('YYYY-MM-DD'), 'days')

  // Check if the selected date is after the 14th day of the month AND within 30 days from now
  if (todayDay > 14 && daysAfterFullMonths < 30) {
    addToast(
      `A fee for ${selectMonthName} will be charged from the hotel. Consider choosing ${nextMonthName} as the start date.`,
      { appearance: 'error' }
    )
  }

  // Check if the selected date is less than 10 days from now
  if (diffDays < 10) {
    addToast(
      ' It will take approximately 2 weeks to complete the campaign registration and participation process. Consider choosing a date at least 10 days from now.',
      { appearance: 'error' }
    )
  }

  setFormData({ serviceStartDate: serviceStartDate })
}

// Handle referrer change in the CreatableSelect component
export const handleChangeReferrer = (newValue, actionMeta, setFormData) => {
  if (actionMeta.action === 'create-option') {
    setFormData({ newReferrerName: newValue.value, referrerId: null })
    return
  }

  if (actionMeta.action === 'clear') {
    setFormData({ referrerId: null, newReferrerName: null })
    return
  }

  if (actionMeta.action === 'select-option') {
    setFormData({ referrerId: newValue.value, newReferrerName: null })
  }
}
